import { useEffect }                from 'react'
import { useForm, Controller }      from 'react-hook-form'
import { zodResolver }              from '@hookform/resolvers/zod'
import { AlertCircle, Loader2 }     from 'lucide-react'
import { createRoleSchema }         from '../schemas/roleSchemas'
import { PermissionMatrix }         from './PermissionMatrix'
import { cn }                       from '@utils'

// ── Field wrapper ─────────────────────────────────────────────────────────────

function Field({ label, error, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ac-text-muted)' }}>
        {label}{required && <span style={{ color: 'var(--ac-danger)' }}> *</span>}
      </label>
      {children}
      {error && (
        <p className="flex items-center gap-1 mt-1 text-xs" style={{ color: 'var(--ac-danger)' }}>
          <AlertCircle size={11} strokeWidth={2} className="flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  )
}

const inputCls = cn(
  'w-full h-10 px-3 rounded-xl border text-sm',
  'focus:outline-none focus:ring-2 focus:ring-[var(--ac-secondary)] focus:ring-inset',
  'transition-colors duration-150',
)

// ── RoleForm ──────────────────────────────────────────────────────────────────

export function RoleForm({ defaultValues, onSubmit, isPending, submitLabel = 'Save Role', isSystem = false }) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver:      zodResolver(createRoleSchema),
    defaultValues: {
      name:        '',
      description: '',
      status:      'active',
      permissions: [],
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (defaultValues) reset({ name: '', description: '', status: 'active', permissions: [], ...defaultValues })
  }, [defaultValues?.id])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Role Name" error={errors.name?.message} required>
          <input
            {...register('name')}
            disabled={isSystem}
            placeholder="e.g. Event Coordinator"
            className={inputCls}
            style={{
              background:   'var(--ac-bg-surface)',
              borderColor:  errors.name ? 'var(--ac-danger)' : 'var(--ac-border-base)',
              color:        'var(--ac-text-base)',
              opacity:      isSystem ? 0.6 : 1,
            }}
          />
          {isSystem && (
            <p className="mt-1 text-xs" style={{ color: 'var(--ac-text-subtle)' }}>
              System role names cannot be changed.
            </p>
          )}
        </Field>

        <Field label="Status" error={errors.status?.message}>
          <select
            {...register('status')}
            className={cn(inputCls, 'pr-8 appearance-none cursor-pointer')}
            style={{ background: 'var(--ac-bg-surface)', borderColor: 'var(--ac-border-base)', color: 'var(--ac-text-base)' }}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </Field>
      </div>

      <Field label="Description" error={errors.description?.message}>
        <textarea
          {...register('description')}
          rows={2}
          placeholder="Brief description of this role's purpose…"
          className="w-full px-3 py-2.5 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--ac-secondary)] focus:ring-inset transition-colors duration-150"
          style={{ background: 'var(--ac-bg-surface)', borderColor: 'var(--ac-border-base)', color: 'var(--ac-text-base)' }}
        />
      </Field>

      {/* Permission Matrix */}
      <div>
        <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ac-text-base)' }}>
          Permissions
        </p>
        <Controller
          name="permissions"
          control={control}
          render={({ field }) => (
            <PermissionMatrix
              permissions={field.value}
              onChange={field.onChange}
              readOnly={false}
            />
          )}
        />
        {errors.permissions && (
          <p className="mt-2 text-xs" style={{ color: 'var(--ac-danger)' }}>{errors.permissions.message}</p>
        )}
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isPending}
          className="inline-flex items-center gap-2 px-6 h-10 rounded-xl text-sm font-semibold disabled:opacity-60 transition-all duration-150"
          style={{
            background:  'linear-gradient(135deg, var(--ac-secondary) 0%, #6D28D9 100%)',
            color:       'var(--ac-secondary-fg)',
            boxShadow:   '0 4px 14px rgba(124,58,237,0.25)',
          }}
        >
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isPending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}
